/**
 * Session layer: ties X3DH and the Double Ratchet to a specific peer device,
 * and decides which wire form (`pkmsg` vs `msg`) a ciphertext should take.
 */
import {
  type KeyPair,
  fromB64,
  timingSafeEqual,
  toB64,
  generateDHKeyPair,
} from './primitives.ts';
import {
  type RatchetState,
  type SerializedRatchet,
  deserializeRatchet,
  initRatchetInitiator,
  initRatchetResponder,
  ratchetDecrypt,
  ratchetEncrypt,
  serializeRatchet,
} from './doubleRatchet.ts';
import { x3dhInitiate, x3dhRespond } from './x3dh.ts';
import { type IdentityState, verifyPreKeyBundle } from './identity.ts';
import type { Ciphertext, PreKeyBundle, PreKeyMessage } from '../types.ts';

export class NoSessionError extends Error {
  peerUserId: string;
  peerDeviceId: number;

  constructor(peerUserId: string, peerDeviceId: number) {
    super(`no session with ${peerUserId}/${peerDeviceId}`);
    this.name = 'NoSessionError';
    this.peerUserId = peerUserId;
    this.peerDeviceId = peerDeviceId;
  }
}

export class UntrustedIdentityError extends Error {
  peerUserId: string;
  newIdentityKey: Uint8Array;

  constructor(peerUserId: string, newIdentityKey: Uint8Array) {
    super(`identity key for ${peerUserId} has changed`);
    this.name = 'UntrustedIdentityError';
    this.peerUserId = peerUserId;
    this.newIdentityKey = newIdentityKey;
  }
}

export interface SessionRecord {
  peerUserId: string;
  peerDeviceId: number;
  /** Their Ed25519 identity key, pinned for the life of the session. */
  theirIdentityKey: Uint8Array;
  ratchet: RatchetState;
  /**
   * Set on the initiator until the peer replies. While present, every outgoing
   * message repeats the X3DH material, because we cannot tell which of our
   * messages actually reached them.
   */
  pending?: { ek: Uint8Array; spkId: number; opkId?: number };
  /**
   * Responder side: the ephemeral key that created this session. Lets us
   * recognise repeat `pkmsg`s from the same handshake instead of tearing the
   * session down and losing the messages already decrypted under it.
   */
  handshakeEk?: Uint8Array;
  createdAt: number;
}

// ---------------------------------------------------------------------------
// Outbound
// ---------------------------------------------------------------------------

/**
 * Begin a session from a fetched prekey bundle.
 *
 * The bundle signature is checked here and nowhere else, so this is the choke
 * point that stops the server substituting its own keys.
 */
export function createOutboundSession(myIdentity: KeyPair, bundle: PreKeyBundle): SessionRecord {
  if (!verifyPreKeyBundle(bundle)) {
    throw new Error(`prekey bundle for ${bundle.userId} failed signature verification`);
  }
  const theirIdentityKey = fromB64(bundle.identityKey);
  const theirSPK = fromB64(bundle.signedPreKey.pub);
  const theirOPK = bundle.oneTimePreKey ? fromB64(bundle.oneTimePreKey.pub) : undefined;

  const ephemeral = generateDHKeyPair();
  const { sharedSecret, associatedData } = x3dhInitiate({
    identity: myIdentity,
    ephemeral,
    theirIdentityPub: theirIdentityKey,
    theirSignedPreKeyPub: theirSPK,
    theirOneTimePreKeyPub: theirOPK,
  });

  return {
    peerUserId: bundle.userId,
    peerDeviceId: bundle.deviceId,
    theirIdentityKey,
    ratchet: initRatchetInitiator(sharedSecret, theirSPK, associatedData),
    pending: { ek: ephemeral.pub, spkId: bundle.signedPreKey.id, opkId: bundle.oneTimePreKey?.id },
    createdAt: Date.now(),
  };
}

/** Encrypt a payload for this peer. Mutates the record's ratchet state. */
export function sealMessage(
  myIdentityPub: Uint8Array,
  record: SessionRecord,
  plaintext: Uint8Array,
): Ciphertext {
  const msg = ratchetEncrypt(record.ratchet, plaintext);
  if (record.pending) {
    return {
      t: 'pkmsg',
      ik: toB64(myIdentityPub),
      ek: toB64(record.pending.ek),
      spkId: record.pending.spkId,
      opkId: record.pending.opkId,
      msg,
    };
  }
  return { t: 'msg', msg };
}

// ---------------------------------------------------------------------------
// Inbound
// ---------------------------------------------------------------------------

export interface OpenResult {
  record: SessionRecord;
  plaintext: Uint8Array;
  /** Set when a `pkmsg` consumed one of our one-time prekeys. */
  consumedOneTimePreKeyId?: number;
  /** True when this ciphertext established a new session. */
  newSession: boolean;
}

/**
 * Decrypt an incoming ciphertext, establishing a session first if it is a
 * `pkmsg`. `existing` is whatever session we already had with that device.
 */
export function openMessage(
  identityState: IdentityState,
  existing: SessionRecord | undefined,
  ciphertext: Ciphertext,
  sender: { userId: string; deviceId: number },
  options: { knownIdentityKey?: Uint8Array } = {},
): OpenResult {
  if (ciphertext.t === 'msg') {
    if (!existing) throw new NoSessionError(sender.userId, sender.deviceId);
    const plaintext = ratchetDecrypt(existing.ratchet, ciphertext.msg);
    // They answered, so the handshake landed and we can stop repeating it.
    existing.pending = undefined;
    return { record: existing, plaintext, newSession: false };
  }

  return openPreKeyMessage(identityState, existing, ciphertext, sender, options);
}

function openPreKeyMessage(
  identityState: IdentityState,
  existing: SessionRecord | undefined,
  ciphertext: PreKeyMessage,
  sender: { userId: string; deviceId: number },
  options: { knownIdentityKey?: Uint8Array },
): OpenResult {
  const theirIdentityKey = fromB64(ciphertext.ik);
  const theirEphemeral = fromB64(ciphertext.ek);

  if (options.knownIdentityKey && !timingSafeEqual(options.knownIdentityKey, theirIdentityKey)) {
    // Either they reinstalled, or someone is impersonating them. The client
    // must ask the user before accepting; we refuse to decide silently.
    throw new UntrustedIdentityError(sender.userId, theirIdentityKey);
  }

  // A repeat of a handshake we already completed: keep the live session.
  if (existing?.handshakeEk && timingSafeEqual(existing.handshakeEk, theirEphemeral)) {
    const plaintext = ratchetDecrypt(existing.ratchet, ciphertext.msg);
    return { record: existing, plaintext, newSession: false };
  }

  const signedPreKey = resolveSignedPreKey(identityState, ciphertext.spkId);
  if (!signedPreKey) {
    throw new Error(`unknown signed prekey id ${ciphertext.spkId}`);
  }
  const oneTimePreKey =
    ciphertext.opkId !== undefined ? identityState.oneTimePreKeys[ciphertext.opkId] : undefined;
  if (ciphertext.opkId !== undefined && !oneTimePreKey) {
    // Already consumed. Usually a duplicate delivery of a message we have.
    throw new Error(`one-time prekey ${ciphertext.opkId} is no longer available`);
  }

  const { sharedSecret, associatedData } = x3dhRespond({
    identity: identityState.identity,
    signedPreKey,
    oneTimePreKey,
    theirIdentityPub: theirIdentityKey,
    theirEphemeralPub: theirEphemeral,
  });

  const record: SessionRecord = {
    peerUserId: sender.userId,
    peerDeviceId: sender.deviceId,
    theirIdentityKey,
    ratchet: initRatchetResponder(sharedSecret, signedPreKey, associatedData),
    handshakeEk: theirEphemeral,
    createdAt: Date.now(),
  };

  // If this throws, the caller keeps its previous session untouched: we built
  // the new record on the side and never mutated `existing`.
  const plaintext = ratchetDecrypt(record.ratchet, ciphertext.msg);

  return {
    record,
    plaintext,
    consumedOneTimePreKeyId: ciphertext.opkId,
    newSession: true,
  };
}

/**
 * The current signed prekey, or the one just rotated out. Accepting the
 * previous key for a grace period stops messages composed seconds before a
 * rotation from becoming permanently undecryptable.
 */
function resolveSignedPreKey(state: IdentityState, id: number): KeyPair | undefined {
  if (state.signedPreKey.id === id) return state.signedPreKey.keys;
  if (state.previousSignedPreKey?.id === id) return state.previousSignedPreKey.keys;
  return undefined;
}

// ---------------------------------------------------------------------------
// Persistence
// ---------------------------------------------------------------------------

export interface SerializedSession {
  peerUserId: string;
  peerDeviceId: number;
  theirIdentityKey: string;
  ratchet: SerializedRatchet;
  pending?: { ek: string; spkId: number; opkId?: number };
  handshakeEk?: string;
  createdAt: number;
}

export function serializeSession(record: SessionRecord): SerializedSession {
  return {
    peerUserId: record.peerUserId,
    peerDeviceId: record.peerDeviceId,
    theirIdentityKey: toB64(record.theirIdentityKey),
    ratchet: serializeRatchet(record.ratchet),
    pending: record.pending
      ? { ek: toB64(record.pending.ek), spkId: record.pending.spkId, opkId: record.pending.opkId }
      : undefined,
    handshakeEk: record.handshakeEk ? toB64(record.handshakeEk) : undefined,
    createdAt: record.createdAt,
  };
}

export function deserializeSession(s: SerializedSession): SessionRecord {
  return {
    peerUserId: s.peerUserId,
    peerDeviceId: s.peerDeviceId,
    theirIdentityKey: fromB64(s.theirIdentityKey),
    ratchet: deserializeRatchet(s.ratchet),
    pending: s.pending
      ? { ek: fromB64(s.pending.ek), spkId: s.pending.spkId, opkId: s.pending.opkId }
      : undefined,
    handshakeEk: s.handshakeEk ? fromB64(s.handshakeEk) : undefined,
    createdAt: s.createdAt,
  };
}
